import React, { useCallback, useState } from "react";
import DeliveryNoteLocationPicker from "./popups/DeliveryNoteLocationPicker";
import PurchasingDocumentDialog from "./popups/PurchasingDocumentDialog";
import MarketplaceProductImage from "./MarketplaceProductImage";
import { marketplaceLineKey } from "../utils/marketplaceSelection";
import { requisitionDisplayQuantity, selectedRequisitionUnit } from "../utils/requisitionUnits";
import "./MarketplaceDestinations.css";

const individuallyAssigned = (line) => line.destination_location_id && line.destination_location_source === "manual";
const withDestination = (line, id, source) => ({ ...line, _marketplaceKey: marketplaceLineKey(line), destination_location_id: id || null, destination_location_source: source });

export default function MarketplaceDestinations({ form, items, units, locations, languageId, text, disabled, onChange, onOpenChange }) {
  const individual = form.destination_mode === "individual" || form.lines.some(individuallyAssigned);
  const [view, setView] = useState(individual ? "individual" : "general");
  const [replacement, setReplacement] = useState(null);
  const opened = useCallback((open) => {
    onOpenChange?.(open);
    if (open) setView(individual ? "individual" : "general");
  }, [onOpenChange, individual]);
  const applyGeneral = (id) => {
    onChange((previous) => ({ ...previous, destination_mode: "general", destination_location_id: id || "",
      lines: previous.lines.map((line) => withDestination(line, null, id ? "header" : "suggested")) }));
    setView("general");
  };
  const setIndividual = () => {
    onChange((previous) => ({ ...previous, destination_mode: "individual", destination_location_id: "",
      lines: previous.lines.map((line) => withDestination(line, individuallyAssigned(line) ? line.destination_location_id : previous.destination_location_id || null,
        individuallyAssigned(line) || previous.destination_location_id ? "manual" : "suggested")) }));
    setView("individual");
  };
  return <DeliveryNoteLocationPicker value={form.destination_location_id} locations={locations} disabled={disabled}
    label={text("Ubicación de entrega", "Delivery location")} resolvedName={individual ? text("Ubicación por artículo", "Location by item") : undefined}
    onOpenChange={opened}
    onChange={(id) => {
      if (form.lines.some(individuallyAssigned)) { setReplacement({ id }); return false; }
      applyGeneral(id);
    }}
    renderContent={({ content, close }) => <>
      <div className="mp-destinationModes" role="group" aria-label={text("Modo de entrega", "Delivery mode")}>
        <button type="button" aria-pressed={view === "general"} onClick={() => setView("general")}>{text("Ubicación general", "General location")}</button>
        <button type="button" aria-pressed={view === "individual"} onClick={setIndividual}>{text("Ubicación por artículo", "Location by item")}</button>
      </div>
      {view === "general" ? <>{individual && <p>{text("Seleccionar una ubicación general sustituye las asignaciones individuales.", "Selecting a general location replaces individual assignments.")}</p>}{content}</> :
        <div className="mp-destinationLines">
          {!form.lines.length && <p>{text("Añade productos al carrito para asignar sus ubicaciones.", "Add products to the cart to assign their locations.")}</p>}
          {form.lines.map((line) => {
            const item = items.find((entry) => Number(entry.id) === Number(line.item_id)) || { id: line.item_id };
            const unit = selectedRequisitionUnit(line, item, units, languageId);
            return <div key={marketplaceLineKey(line)} className="mp-destinationLine" data-destination-item={line.item_id}>
              <div className="mp-destinationPhoto"><MarketplaceProductImage item={item} compact text={text} /></div>
              <div><strong>{item.name || line.description}</strong><small>{item.code}</small><span>{Number(requisitionDisplayQuantity(line, unit)).toLocaleString(languageId, { maximumFractionDigits: 8 })} {unit?.label}</span></div>
              <DeliveryNoteLocationPicker value={line.destination_location_source === "manual" ? line.destination_location_id : null} locations={locations}
                label={`${text("Destino", "Destination")}: ${item.name || line.item_id}`} onChange={(id) => onChange((previous) => ({ ...previous,
                  lines: previous.lines.map((entry) => marketplaceLineKey(entry) === marketplaceLineKey(line) ? withDestination(entry, id, id ? "manual" : "suggested") : entry) }))} />
            </div>;
          })}
        </div>}
      <PurchasingDocumentDialog isOpen={Boolean(replacement)} title={text("Sustituir ubicaciones individuales", "Replace individual locations")}
        message={text("La ubicación general sustituirá todas las asignaciones individuales del carrito. ¿Continuar?", "The general location will replace all individual assignments in the cart. Continue?")}
        onClose={() => setReplacement(null)} onConfirm={() => { applyGeneral(replacement.id); setReplacement(null); close(); }} />
    </>} />;
}
